import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArmadaNav } from "@/components/ArmadaNav";
import {
  Radar, Globe, Split, Hammer, TrendingUp, Lock, CheckCircle2, Sparkles,
  Zap, ArrowRight, Crown, AlertCircle, Loader2,
} from "lucide-react";

const ICON_MAP = {
  radar: Radar,
  globe: Globe,
  split: Split,
  hammer: Hammer,
  "trending-up": TrendingUp,
};

const TIER_HUE = {
  "Tier I":   { from: "from-emerald-500/15", to: "to-transparent", border: "border-emerald-400/30", text: "text-emerald-300" },
  "Tier II":  { from: "from-sky-500/15",     to: "to-transparent", border: "border-sky-400/30",     text: "text-sky-300" },
  "Tier III": { from: "from-fuchsia-500/15", to: "to-transparent", border: "border-fuchsia-400/30", text: "text-fuchsia-300" },
  "Tier IV":  { from: "from-amber-500/15",   to: "to-transparent", border: "border-amber-400/30",   text: "text-amber-300" },
  "Tier V":   { from: "from-rose-500/15",    to: "to-transparent", border: "border-rose-400/30",    text: "text-rose-300" },
};

function fmtMoney(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

export default function CommandShopPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shop/builds");
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Show success / cancel toast from Stripe redirect
  useEffect(() => {
    const status = params.get("status");
    const build = params.get("build");
    if (status === "success") setToast({ kind: "ok", msg: `Build unlocked: ${build}. Welcome to the advanced fleet.` });
    if (status === "cancel") setToast({ kind: "warn", msg: "Checkout cancelled. No charges made." });
    if (status) setTimeout(() => setToast(null), 7000);
  }, [params]);

  const accelerate = async (build) => {
    setBusy(build.id); setError("");
    try {
      const { data } = await api.post("/shop/checkout", { build_id: build.id });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(formatApiError(e));
      setBusy(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-armada-base">
      <ArmadaNav />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <div className="mb-10 animate-fade-up">
          <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2 flex items-center gap-2">
            <Crown className="w-3 h-3" /> COMMAND SHOP · ADVANCED BUILDS
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
            Forge the <span className="gold-shimmer">Next Hull</span>
          </h1>
          <p className="text-sm text-zinc-400 mt-3 font-light max-w-2xl">
            Unlock advanced capabilities for the Armada. Achieve lifetime revenue benchmarks for free unlocks, or accelerate immediately via Stripe — 100% of credit purchases flow to the Fleet Treasury.
          </p>
          {data && (
            <div className="mt-5 inline-flex items-center gap-3 px-4 py-2.5 rounded-md glass border border-[#D4AF37]/20" data-testid="shop-lifetime-revenue">
              <span className="text-[10px] tracking-armada text-zinc-500 font-bold">FLEET LIFETIME REVENUE</span>
              <span className="text-lg font-black gold-shimmer">${data.lifetime_revenue.toFixed(2)}</span>
            </div>
          )}
        </div>

        {toast && (
          <div data-testid="shop-toast" className={`mb-6 flex items-start gap-2 p-3 rounded-md text-sm animate-fade-up ${
            toast.kind === "ok" ? "bg-emerald-950/40 border border-emerald-900/50 text-emerald-200"
            : "bg-amber-950/40 border border-amber-900/50 text-amber-200"
          }`}>
            <CheckCircle2 className="w-4 h-4 mt-0.5" /><span>{toast.msg}</span>
          </div>
        )}
        {error && (
          <div className="mb-6 flex items-start gap-2 p-3 rounded-md bg-red-950/40 border border-red-900/50 text-red-200 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5" /><span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {data?.builds?.map((b, i) => {
            const Icon = ICON_MAP[b.icon] || Sparkles;
            const hue = TIER_HUE[b.tier] || TIER_HUE["Tier I"];
            return (
              <div
                key={b.id}
                data-testid={`shop-build-${b.id}`}
                className={`glass-strong rounded-xl p-6 border ${b.unlocked ? "border-emerald-500/40" : hue.border} bg-gradient-to-br ${hue.from} ${hue.to} animate-fade-up stagger-${(i % 7) + 1} flex flex-col`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg ${b.unlocked ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-black/40 border border-white/10"} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${b.unlocked ? "text-emerald-400" : hue.text}`} strokeWidth={2} />
                  </div>
                  <div className="text-right">
                    <div className={`text-[10px] tracking-armada font-bold ${hue.text}`}>{b.tier}</div>
                    {b.unlocked ? (
                      <span data-testid={`shop-status-${b.id}`} className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                        <CheckCircle2 className="w-3 h-3" /> Unlocked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-zinc-900/60 text-zinc-400 border border-white/10">
                        <Lock className="w-3 h-3" /> Locked
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="text-xl font-black tracking-tight text-white">{b.name}</h3>
                <p className={`text-xs ${hue.text} mt-1 font-semibold`}>{b.tagline}</p>
                <p className="text-sm text-zinc-400 mt-3 font-light leading-relaxed flex-1">{b.description}</p>

                <div className="mt-5 space-y-3">
                  {/* FREE path */}
                  <div>
                    <div className="flex items-center justify-between text-[10px] tracking-wider uppercase text-zinc-500 font-bold mb-1.5">
                      <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Free at {fmtMoney(b.benchmark)} Lifetime Revenue</span>
                      <span className={b.unlocked && b.unlocked_via === "benchmark" ? "text-emerald-400" : "text-zinc-500"}>{b.progress_pct}%</span>
                    </div>
                    <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-700 ${b.unlocked && b.unlocked_via === "benchmark" ? "bg-emerald-400" : "bg-gradient-to-r from-[#A88729] to-[#D4AF37]"}`}
                        style={{ width: `${b.progress_pct}%` }}
                      />
                    </div>
                  </div>

                  {/* ACCELERATE path */}
                  {!b.unlocked && (
                    <button
                      data-testid={`shop-accelerate-${b.id}`}
                      onClick={() => accelerate(b)}
                      disabled={busy === b.id}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-md bg-[#D4AF37] text-black font-bold hover:bg-[#E5C158] transition-all shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        {busy === b.id ? "Redirecting…" : `Accelerate · ${fmtMoney(b.price)}`}
                      </span>
                      {busy === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    </button>
                  )}
                  {b.unlocked && (
                    <div className="text-[11px] text-emerald-300 font-mono">
                      Unlocked via <span className="font-bold uppercase">{b.unlocked_via}</span>
                      {b.unlocked_at && ` · ${new Date(b.unlocked_at).toLocaleDateString()}`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {!data && (
            <div className="md:col-span-2 xl:col-span-3 text-center text-zinc-500 py-12">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#D4AF37]" />
              <div className="mt-3 text-sm">Loading advanced builds…</div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
